import React from 'react'
import { Card, Row } from 'react-bootstrap'
import { AccordionSection, AccordionCard } from '../../accordion'
import { formatCurrency, IsSpecialEmployer } from 'application/helpers'
import { Tabs } from '@mmsg/louie'
import ColItem from './colItem'
import * as S from './estimateDetailElements'
import globals from 'application/constants/globals'
import { EstimateDetailStaticData } from './estimateDetailStaticData'

interface IInputProps {
  regoInsPerPay: number
  payFrequency: string
  regoPerPay: number
  compInsPerPay: number
  regoPerAnnum: number
  compInsPerAnnum: number
  registrationSubtext?: string
  compInsuranceSubtext?: string
  compInsuranceProvider: string
  compInsurancePremiumFinanced: boolean
  isExGST: boolean
}

const RegoAndInsurance = (props: IInputProps) => {
  interface IRegInsTableProps {
    rego: number
    compIns: number
    id: string
    isExGST: boolean
    regoSubtext?: string
    compSubtext?: string
  }

  const RegistrationInsuranceTable = (props: IRegInsTableProps) => {
    return (
      <Row>
        <ColItem
          icon={globals.isBrandMX ? 'Registration' : 'IconTick'}
          id={`${props.id}-annual-registration`}
          title="Registration"
          value={props.rego}
          tooltip={{
            subHeading: props.regoSubtext,
            text: 'To drive a car in Australia it needs to be re-registered each year. We make paying for registration easy by budgeting for it across the life of your lease. We can pay it on your behalf from your budget, or you can pay it upfront and claim it back.',
          }}
          isExGST={props.isExGST}
        />
        <ColItem
          icon={globals.isBrandMX ? 'Insurance' : 'ComprehensiveInsurance'}
          iconSize={globals.isBrandMX ? undefined : 2.1}
          id={`${props.id}-comprehensive-insurance`}
          title="Comprehensive insurance"
          value={props.compIns}
          tooltip={{
            subHeading: props.compSubtext,
            text: 'All cars under a Novated Lease must be covered by Comprehensive Insurance. So you don’t have to worry about finding the funds year after year, we budget for the annual renewal over the life of your lease.',
          }}
          isExGST={props.isExGST}
        />
      </Row>
    )
  }

  let isSAGBYO = IsSpecialEmployer('SAGBYO')
  
  return (
    <Card className="mb-13rem card-shadow">
      <Card.Header className="border-bottom-0 d-flex justify-content-between">
        <S.CardHeader>Registration and car insurance</S.CardHeader>
        <S.CardHeaderValue id="runningsection.registrationperpay">
          {formatCurrency(props.regoInsPerPay)}
        </S.CardHeaderValue>
      </Card.Header>
      <Card.Body>
        <AccordionSection
          readerText="Registration and car insurance"
          expandAccordion={false}
          title={EstimateDetailStaticData('RegoAndInsurance')}
          id="runningsection.registrationinsuranceaccordion"
          defaultActive={false}
        >
          <Tabs
            id="runningsection.registrationinsurancetab"
            data={[
              {
                title: props.payFrequency,
                content: () => (
                  <RegistrationInsuranceTable
                    rego={props.regoPerPay}
                    regoSubtext={props.registrationSubtext}
                    compIns={props.compInsPerPay}
                    compSubtext={props.compInsuranceSubtext}
                    id="rego-compIns-perPay"
                    isExGST={props.isExGST}
                  />
                ),
              },
              {
                title: 'Annually',
                content: () => (
                  <RegistrationInsuranceTable
                    rego={props.regoPerAnnum}
                    compIns={props.compInsPerAnnum}
                    id={`rego-compIns-Annually`}
                    isExGST={props.isExGST}
                  />
                ),
              },
            ]}
            activeIndex={0}
            equalWidth
          />

          <p className={globals.isBrandRS ? 'text-small' : ''}>
            Disclaimer: Your registration and insurance renewals can be impacted
            by the time of year you start your novated lease, the insurance and
            renewal that came with the car, as well as other factors.
            {props.compInsuranceProvider === 'Allianz' && props.compInsurancePremiumFinanced && props.compInsPerPay > 0 &&
              ' You have the choice of repairer. Please refer to your PDS for details about choice of repairer.'}
          </p>
        </AccordionSection>

        {!isSAGBYO && 
          <AccordionCard
            expandAccordion={false}
            className="mb-24"
            title={'Do I have to use ' + globals.brand + ' insurance?'}
            id="runningsection.do ihavetoaccordion"
            smallerTitleText={false}
            defaultActive={false}
          >
            <S.BrandDefaultText>
              No. Comprehensive Vehicle Insurance is required for all leases but
              you’re in total control when it comes to choosing your insurance.
              Let your consultant know your annual premium and they’ll work it
              into your budget. If you do decide to take out one of the options
              presented, we’ll take care of the admin for you. Meaning you can get
              back to whatever you’d rather be doing than chatting about
              insurance.
            </S.BrandDefaultText>
          </AccordionCard>
        }
      </Card.Body>
    </Card>
  )
}

export default RegoAndInsurance
